import React from 'react';
import { useField } from 'formik';
import { InputGroup, Input as ReactstrapInput, FormFeedback } from 'reactstrap';
import { useTranslator } from '@front-activerse/common-components';

interface AmountWithUnitInputProps {
  amountName: string;
  unitName: string;
  availableUnits: string[];
  currentUnit: string;
  onUnitChange: (newUnit: string) => void;
  onAmountChange?: () => void;
  step?: number;
  translateErrMsg?: boolean;
  disableMetaTouched?: boolean;
}

const AmountWithUnitInput: React.FC<AmountWithUnitInputProps> = ({
  amountName,
  unitName,
  availableUnits,
  currentUnit,
  onUnitChange,
  onAmountChange,
  step = 0.1,
  translateErrMsg = false,
  disableMetaTouched = false,
}) => {
  const [amountField, amountMeta, amountHelpers] = useField(amountName);
  const [unitField] = useField(unitName);
  const { t } = useTranslator();

  console.debug('[AmountWithUnitInput]', {
    amountName,
    valueFromFormik: amountField.value,
    valueType: typeof amountField.value,
  });

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      amountHelpers.setValue('');
    } else {
      const number = +value;
      let roundedValue: number;
      switch (step) {
        case 1:
          roundedValue = Math.round(number);
          break;
        case 0.1:
          roundedValue = Math.round(number * 10) / 10;
          break;
        case 0.01:
          roundedValue = Math.round(number * 100) / 100;
          break;
        default:
          roundedValue = Math.round(number * 100) / 100;
          break;
      }
      amountHelpers.setValue(roundedValue);
    }
    if (onAmountChange) {
      onAmountChange();
    }
  };

  // automatically select all text when input is focused and is number input
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    requestAnimationFrame(() => {
      input.select();
    });
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUnit = e.target.value;
    unitField.onChange(e);
    onUnitChange(newUnit);
  };

  if (disableMetaTouched) {
    amountMeta.touched = true;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
      <InputGroup
        className={
          amountMeta.touched && amountMeta.error ? 'is-invalid flex-nowrap' : 'flex-nowrap'
        }
        style={{ overflow: 'hidden', width: 'auto' }}
      >
        <ReactstrapInput
          {...amountField}
          type="number"
          step={step}
          invalid={!!(amountMeta.touched && amountMeta.error)}
          onChange={handleAmountChange}
          onFocus={handleFocus}
          style={{
            minWidth: '60px',
            maxWidth: '60px',
          }}
        />
        {availableUnits.length > 1 ? (
          <ReactstrapInput
            {...unitField}
            type="select"
            value={currentUnit}
            onChange={handleUnitChange}
            className="unit-select"
            style={{
              minWidth: '80px',
              maxWidth: '80px',
            }}
          >
            {availableUnits.map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </ReactstrapInput>
        ) : (
          <ReactstrapInput type="text" value={currentUnit} disabled />
        )}
      </InputGroup>
      {amountMeta.touched && amountMeta.error && (
        <FormFeedback style={{ display: 'block', marginTop: '4px' }}>
          {translateErrMsg ? t(amountMeta.error) : amountMeta.error}
        </FormFeedback>
      )}
    </div>
  );
};

export default AmountWithUnitInput;
